import _ from 'lodash';
import moment from 'moment';
import {Q} from '@nozbe/watermelondb';
import database from 'src/providers/watermelondb';

type ExportTBTArgs = {
  sets: any[]; // can be all sets or just current target’s sets
  events?: any[]; // programEvents / recentEvents from wrapper (optional)
  useObservables?: boolean; // same flag you use in the wrapper
  reportScreen?: boolean; // same flag you pass into wrapper
  targetId: string; // currentTarget
  trialCount: number; // program?.numberOfTrials
  session?: any; // so Date column mirrors UI
  startDate?: string | number | Date; // optional filter (kept for parity)
  endDate?: string | number | Date; // optional filter (kept for parity)
};

export const exportTrialByTrial = async ({
  sets = [],
  events = [],
  useObservables = false,
  reportScreen = false,
  targetId,
  trialCount = 10,
  session,
  startDate,
  endDate,
}: ExportTBTArgs) => {
  // ---------- helpers ----------
  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);
  const setEnd = (s: any) => val(s?.endTimestamp, s?.end_timestamp ?? s?.end);
  const setTid = (s: any) => val(s?.target?.id, s?.target_id ?? s?.targetId);

  const normalizePromptsArray = (ps: any): any[] => {
    if (Array.isArray(ps)) {
      return ps;
    }
    if (ps == null) {
      return [];
    }
    if (typeof ps === 'string') {
      try {
        const parsed = JSON.parse(ps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        if (ps.includes(',')) {
          return ps
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
        return [ps];
      }
    }
    return [];
  };

  const createdTs = (e: any): number => {
    const t = e?.created_at ?? e?.createdAt ?? 0;
    if (typeof t === 'number') {
      return t;
    }
    const n = Number(t);
    if (!isNaN(n)) {
      return n;
    }
    const d = new Date(t);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  // ---------- pick sets for the requested target & optional date range ----------
  const allSetsForTarget = sets.filter(
    s => String(setTid(s)) === String(targetId),
  );

  const filteredSets =
    startDate && endDate
      ? allSetsForTarget.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : allSetsForTarget;

  const sortedSets = filteredSets;

  // ---------- detect event shape & key by set id (mirror wrapper logic) ----------
  const keyBySet = (() => {
    // If reportScreen OR !useObservables → 'set_id'
    // Else → 'set.id'
    if (reportScreen || !useObservables) {
      return (e: any) => String(e?.set_id ?? '');
    }
    return (e: any) => String(e?.set?.id ?? '');
  })();

  // Only keep events for sets we’re exporting
  const setIds = new Set(sortedSets.map(s => String(s?.id ?? '')));
  const allEvents = (events || []).filter(e => setIds.has(keyBySet(e)));
  const eventsBySetId: Record<string, any[]> = _.groupBy(allEvents, keyBySet);

  // ---------- prefetch prompt acronyms (from events’ prompts) ----------
  const collectPromptIds = (): string[] => {
    const ids = new Set<string>();
    for (const s of sortedSets) {
      for (const e of eventsBySetId[String(s?.id ?? '')] || []) {
        for (const p of normalizePromptsArray(e?.prompts)) {
          if (typeof p === 'string') {
            ids.add(p);
          } else if (p?.id) {
            ids.add(p.id);
          }
        }
      }
    }
    return Array.from(ids);
  };

  const promptAcronymById: Record<string, string> = {};
  const promptIds = collectPromptIds();

  const fetchByIdBatches = async (
    table: string,
    ids: string[],
    idField: string = 'id',
  ) => {
    const out: any[] = [];
    for (const ch of _.chunk(ids, 500)) {
      const _rows = await database
        .get(table)
        .query(Q.where(idField, Q.oneOf(ch)))
        .fetch();
      out.push(..._rows);
    }
    return out;
  };

  if (promptIds.length > 0) {
    // primary: prompts
    const base = await fetchByIdBatches('prompts', promptIds, 'id');
    for (const r of base) {
      const acr = r?.acronym || r?.name || '';
      if (acr) {
        promptAcronymById[r.id] = acr;
      }
    }
    // fallback: program_prompt (may carry acronym/name)
    const missing = promptIds.filter(id => !promptAcronymById[id]);
    if (missing.length > 0) {
      const pp = await fetchByIdBatches('program_prompt', missing, 'id');
      for (const p of pp) {
        const acr = p?.acronym || p?.name || '';
        if (acr) {
          promptAcronymById[p.id] = acr;
        }
      }
      // hop via prompt_id -> prompts
      const stillMissing = missing.filter(id => !promptAcronymById[id]);
      if (stillMissing.length > 0 && pp.length > 0) {
        const baseIds = _.uniq(
          pp.map((x: any) => x?.prompt_id || x?.promptId).filter(Boolean),
        );
        if (baseIds.length > 0) {
          const basePrompts = await fetchByIdBatches('prompts', baseIds, 'id');
          const baseMap: Record<string, string> = {};
          for (const b of basePrompts) {
            baseMap[b.id] = b?.acronym || b?.name || '';
          }
          for (const p of pp) {
            const acr = baseMap[p?.prompt_id || p?.promptId];
            if (acr && !promptAcronymById[p.id]) {
              promptAcronymById[p.id] = acr;
            }
          }
        }
      }
    }
  }

  const getPromptAcronyms = (ev: any): string[] => {
    const ps = normalizePromptsArray(ev?.prompts);
    const out: string[] = [];
    for (const p of ps) {
      if (typeof p === 'string') {
        if (promptAcronymById[p]) {
          out.push(promptAcronymById[p]);
        }
      } else if (p?.acronym) {
        out.push(p.acronym);
      } else if (p?.name) {
        out.push(p.name);
      } else if (p?.id && promptAcronymById[p.id]) {
        out.push(promptAcronymById[p.id]);
      }
    }
    return out.filter(Boolean);
  };

  // ---------- header ----------
  // We can fetch the target name for the header, but most wrappers already have it.
  // To avoid another DB roundtrip, the header’s first cell can be filled later by caller
  // if needed. For parity with your original, we’ll try to fetch it quickly:
  let targetName = '';
  try {
    const t = await database.get('targets').find(String(targetId));
    targetName = t?.name || '';
  } catch {
    targetName = '';
  }

  const rows: any[] = [
    [
      targetName,
      'Date',
      'Time Stamp',
      'Set',
      ..._.range(0, trialCount || 10).map((__, i) => `${i + 1}`),
      'Score',
    ],
  ];

  // ---------- rows ----------
  for (let i = 0; i < sortedSets.length; i++) {
    const set = sortedSets[i];
    const sid = String(set?.id ?? '');
    const evs: any[] = (eventsBySetId[sid] || [])
      .slice()
      .sort((a, b) => createdTs(a) - createdTs(b));

    // Date: session?.sessionDate else set.session_date (UI parity)
    const dateStr = session
      ? moment(session?.sessionDate).format('ll')
      : moment(set?.session_date).format('ll');

    const startTs = setStart(set);
    const endTs = setEnd(set);

    const timeStamp =
      `${startTs ? moment(startTs).format('hh:mm:ss A') : ''} - ` +
      `${endTs ? moment(endTs).format('hh:mm:ss A') : 'Incomplete'}`;

    const row: any[] = [
      '', // first column is header target name; keep empty per original
      dateStr, // Date
      timeStamp, // Time Stamp
      `Set ${i + 1}`, // Set (index within this target)
    ];

    // Trials 1..trialCount: map to chronological events
    for (let t = 0; t < (trialCount || 10); t++) {
      const event = evs[t];
      let icon = '-';
      if (event) {
        if (event?.value === 'success') {
          icon = 'O';
        } else if (event?.value === 'error') {
          const acrs = getPromptAcronyms(event);
          icon = acrs.length ? ['X', ...acrs].join('\n') : 'X';
        } else {
          icon = '-';
        }
      }
      row.push(icon);
    }

    // Score column
    const total = evs.length || 0;
    const succ = evs.filter(e => e?.value === 'success').length;
    const score = total > 0 ? `${Math.ceil((succ / total) * 100)}%` : '0%';
    row.push(score);

    rows.push(row);
  }

  return rows;
};
